import { FC, PropsWithChildren, createContext, useCallback, useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router";

import { getMemberEligibility } from "../api/eligibility";
import { listMemberDataholders } from "../api/member";
import { AuthSources, SearchParamKeys, StorageKeys } from "../constants/constants";
import { useNavigateClient } from "../hooks";
import { ANALYTIC_EVENTS, logEvent } from "../service/analyticsService";
import { MemberEligibilityFlag, MemberEligibilityInfo } from "../types";
import { AuthenticationContext } from "./AuthenticationContext";

const DEFAULT_ELIGIBILITY: MemberEligibilityInfo = {
  group: {
    id: 0,
    end_date: "",
    start_date: "",
    external_group_id: "",
    group_number: "",
  },
  member: {
    id: 0,
    external_member_id: "",
    start_date: "",
    end_date: "",
    opt_in_to_emails: false,
    opt_in_to_push_notifications: false,
    selected_notification_authenticator_id: null,
    is_minor_dependent: false,
  },
  plan: {
    id: 0,
  },
  plan_account: {
    id: 0,
    end_date: "",
    start_date: "",
    plan_account_id: 0,
    group_number: "",
    external_account_id: "",
    name: "",
  },
  plan_benefit: {
    id: 0,
  },
  formulary: {},
  member_flags: [],
};

interface MemberEligibilityContextType {
  readonly memberEligibility: MemberEligibilityInfo;
  readonly loggedInEligibility: MemberEligibilityInfo;
  readonly availableEligibility: MemberEligibilityInfo[];
  readonly isViewingDataholder: boolean;
  readonly isLoadingEligibility: boolean;
  readonly setActiveMemberEligibility: (availableEligibility: MemberEligibilityInfo) => void;
  readonly updateMemberEligibilityWithFlags: (flags: Array<MemberEligibilityFlag>) => void;
}

const DEFAULT_ELIGIBILITY_CONTEXT: MemberEligibilityContextType = {
  memberEligibility: DEFAULT_ELIGIBILITY,
  loggedInEligibility: DEFAULT_ELIGIBILITY,
  availableEligibility: [],
  isViewingDataholder: false,
  setActiveMemberEligibility: () => {
    return;
  },
  updateMemberEligibilityWithFlags: () => {
    return;
  },
  isLoadingEligibility: true,
};

export const MemberEligibilityContext = createContext<MemberEligibilityContextType>(DEFAULT_ELIGIBILITY_CONTEXT);

export const MemberEligibilityProvider: FC<PropsWithChildren> = ({ children }): JSX.Element => {
  const [memberEligibility, setMemberEligibility] = useState<MemberEligibilityInfo>(DEFAULT_ELIGIBILITY);
  const [selectedEligibility, setSelectedEligibility] = useState<MemberEligibilityInfo>(DEFAULT_ELIGIBILITY);
  const [availableEligibility, setAvailableEligibility] = useState<MemberEligibilityInfo[]>([DEFAULT_ELIGIBILITY]);
  const [isLoadingEligibility, setIsLoadingEligibility] = useState<boolean>(true);
  const [hasSentLoginAnalytics, setHasSentLoginAnalytics] = useState<boolean>(false);
  const [loggedInEligibility, setLoggedInEligibility] = useState<MemberEligibilityInfo>(DEFAULT_ELIGIBILITY);
  const [isViewingDataholder, setIsViewingDataholder] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const { authState, logoutUser, getAuthToken } = useContext(AuthenticationContext);
  const navigate = useNavigateClient();

  const setActiveMemberEligibility = useCallback(
    (eligibility: MemberEligibilityInfo) => {
      setSelectedEligibility(eligibility);
      setIsViewingDataholder(eligibility.member.id !== loggedInEligibility.member.id);
    },
    [memberEligibility, loggedInEligibility],
  );

  const fetchAndSetAllEligibility = useCallback(async () => {
    setIsLoadingEligibility(true);
    const authToken = await getAuthToken();
    const initialEligibility = await getMemberEligibility(authToken);
    const dataholderEligibilities = await listMemberDataholders(authToken);
    const availElig = [initialEligibility, ...dataholderEligibilities.results];
    setAvailableEligibility(availElig);

    /**
     * Sometimes we come into a new tab while viewing PHI of another member
     * In this case, we want the app to load the new tab as if it were the
     * dataholder we were viewing in the last tab.
     */
    const enforcedId = searchParams.get(SearchParamKeys.MEMBER_ELIGIBILITY_MEMBER_ID);

    if (enforcedId) {
      const matchingElig = availElig.find((elig) => elig.member.id === +enforcedId);
      setMemberEligibility(matchingElig || initialEligibility);
      setSelectedEligibility(matchingElig || initialEligibility);
      setIsViewingDataholder(!!matchingElig);
    } else {
      setMemberEligibility(initialEligibility);
      setSelectedEligibility(initialEligibility);
      setIsViewingDataholder(false);
    }
    setLoggedInEligibility(initialEligibility);

    if (!hasSentLoginAnalytics) {
      if (localStorage.getItem(StorageKeys.AUTH_SOURCE) === AuthSources.CAPITALRX_STS) {
        logEvent(ANALYTIC_EVENTS.LOGIN.ssoSuccess);
      } else {
        logEvent(ANALYTIC_EVENTS.LOGIN.success);
      }
      setHasSentLoginAnalytics(true);
    }
    setIsLoadingEligibility(false);
  }, [hasSentLoginAnalytics, getAuthToken]);

  const updateMemberEligibilityWithFlags = useCallback(
    (flags: Array<MemberEligibilityFlag>) => {
      // amend the member flags on the selected eligibility
      const newSelectedEligibility = { ...selectedEligibility, member_flags: flags };

      // update the selected eligibility
      setSelectedEligibility(newSelectedEligibility);

      // update the available eligibilities
      const selectedMemberId = selectedEligibility.member.id;
      const newAvailableEligibilities = availableEligibility.map((elig) => {
        if (elig.member.id === selectedMemberId) {
          return newSelectedEligibility;
        } else {
          return elig;
        }
      });
      setAvailableEligibility(newAvailableEligibilities);
    },
    [memberEligibility, availableEligibility],
  );

  /**
   * We want to fetch the currently logged in user's eligibility first,
   * then call out to the dataholder list endpoint to see if there's any other
   * elibilities this member has access to.
   *
   * We should only ever do this ONCE, as it is expensive to make that call.
   */
  useEffect(() => {
    const shouldCheckEligibility = authState === "signedIn" && (memberEligibility?.member?.id || 0) === 0 && !isLoadingEligibility;
    if (shouldCheckEligibility) {
      setIsLoadingEligibility(true);
      fetchAndSetAllEligibility().catch(() => {
        logoutUser().then(() => {
          setMemberEligibility(DEFAULT_ELIGIBILITY);
          navigate("/login");
          setIsLoadingEligibility(false);
        });
      });
    } else {
      setIsLoadingEligibility(false);
    }
  }, [authState]);

  useEffect(() => {
    const isCleanupAuthState = !["signedIn", "loading"].includes(authState);
    if (isCleanupAuthState && (memberEligibility?.member?.id || 0) !== 0) {
      setMemberEligibility(undefined);
      setSelectedEligibility(undefined);
      setAvailableEligibility([]);
      setIsViewingDataholder(false);
      setHasSentLoginAnalytics(false);
    }
  }, [authState]);

  return (
    <MemberEligibilityContext.Provider
      value={{
        availableEligibility: availableEligibility,
        loggedInEligibility: loggedInEligibility,
        memberEligibility: selectedEligibility,
        isViewingDataholder: isViewingDataholder,
        isLoadingEligibility: isLoadingEligibility,
        setActiveMemberEligibility: setActiveMemberEligibility,
        updateMemberEligibilityWithFlags: updateMemberEligibilityWithFlags,
      }}
    >
      {children}
    </MemberEligibilityContext.Provider>
  );
};
